import { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHome,
  faUsers,
  faMessage,
  faFileInvoiceDollar,
  faAddressBook,
  faMoneyBillWave,
  faExclamationTriangle,
  faFileAlt,
  faBullseye,
  faQrcode,
  faBuilding,
  faUpload,
  faUserShield,
  faChevronDown,
  faChevronUp,
} from "@fortawesome/free-solid-svg-icons";
import { NavLink } from "react-router-dom";
import logo from "../assets/images/logo.png";
import translations from "../Translation/Sidebar.json";
import { adminListZones } from "../api";

export default function Sidebar({ sidebarOpen, setSidebarOpen }) {
  const [openDropdown, setOpenDropdown] = useState(null);
  const [openZone, setOpenZone] = useState(null);
  const [zones, setZones] = useState([]);
  const [zonesLoading, setZonesLoading] = useState(false);

  const toggleDropdown = (menu) => {
    setOpenDropdown(openDropdown === menu ? null : menu);
  };

  const toggleZone = (zoneId) => {
    setOpenZone(openZone === zoneId ? null : zoneId);
  };

  useEffect(() => {
    const loadZones = async () => {
      try {
        setZonesLoading(true);
        const res = await adminListZones("perumahan");
        setZones(Array.isArray(res) ? res : []);
      } catch (error) {
        console.error("Failed to load zones:", error);
        setZones([]);
      } finally {
        setZonesLoading(false);
      }
    };

    loadZones();
  }, []);

  const linkClass = ({ isActive }) =>
    `flex items-center py-2 px-3 rounded-md text-sm font-medium transition duration-200 ${
      isActive
        ? "bg-blue-700 text-white shadow-md"
        : "hover:bg-blue-100 hover:text-blue-700 text-gray-700"
    }`;

  const subLinkClass = ({ isActive }) =>
    `block py-1.5 px-3 rounded-md text-sm ${
      isActive
        ? "bg-blue-700 text-white"
        : "text-gray-700 hover:bg-blue-100 hover:text-blue-700"
    }`;

  return (
    <aside
      className={`fixed z-50 top-0 left-0 w-64 min-h-screen bg-white shadow-lg rounded-r-xl transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0 md:static`}
    >
      <div className="px-4 py-5 border-b">
        <img src={logo} alt="logo" className="w-30 h-10" />
        <h2 className="text-lg font-bold mt-2 text-blue-900 tracking-wide">
          {translations.sidebar.appName}
        </h2>
      </div>

      <nav className="p-3 space-y-1 overflow-y-auto max-h-[calc(100vh-110px)]">
        <NavLink to="/" end onClick={() => setSidebarOpen(false)} className={linkClass}>
          <FontAwesomeIcon icon={faHome} className="mr-3 text-blue-500" />
          LAMAN UTAMA
        </NavLink>

        <div>
          <button
            onClick={() => toggleDropdown("penghuni")}
            className="flex items-center justify-between w-full py-2 px-3 rounded-md text-sm font-medium text-gray-700 hover:bg-indigo-100 hover:text-indigo-700 transition"
          >
            <span className="flex items-center">
              <FontAwesomeIcon icon={faUsers} className="mr-3 text-purple-500" />
              DATA PENGHUNI PA/PPR
            </span>
            <FontAwesomeIcon
              icon={openDropdown === "penghuni" ? faChevronUp : faChevronDown}
              className="text-gray-500 text-xs"
            />
          </button>

          {openDropdown === "penghuni" && (
            <div className="ml-4 mt-2 max-h-96 overflow-y-auto pr-1 space-y-2">
              {zonesLoading ? (
                <div className="py-1.5 px-3 text-sm text-gray-500">Loading...</div>
              ) : zones.length === 0 ? (
                <div className="py-1.5 px-3 text-sm text-gray-500">Tiada zone</div>
              ) : (
                zones.map((zone) => {
                  const zoneId = zone.zone_id || zone.zone_name;
                  const zoneName = zone.zone_name || zone.zone_id;
                  const kawasans = Array.isArray(zone.kawasans) ? zone.kawasans : [];

                  return (
                    <div key={zoneId} className="rounded-md">
                      <button
                        onClick={() => toggleZone(zoneId)}
                        className="w-full flex items-center justify-between px-3 py-2 text-left text-sm font-medium text-gray-700 hover:bg-blue-50 rounded-md"
                      >
                        <span className="truncate">{zoneName}</span>
                        <FontAwesomeIcon
                          icon={openZone === zoneId ? faChevronUp : faChevronDown}
                          className="text-gray-500 text-xs ml-2"
                        />
                      </button>

                      {openZone === zoneId && (
                        <div className="ml-3 mt-1 space-y-1 border-l pl-2">
                          {kawasans.map((kawasan) => {
                            const kawasanId = kawasan.kawasan_id || kawasan.kawasan_name;
                            const kawasanName = kawasan.kawasan_name || kawasan.kawasan_id;

                            return (
                              <NavLink
                                key={`${zoneId}-${kawasanId}`}
                                to={`/data-penghuni/${encodeURIComponent(kawasanId)}`}
                                onClick={() => setSidebarOpen(false)}
                                className={subLinkClass}
                              >
                                {kawasanName}
                              </NavLink>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>

        <div>
          <button
            onClick={() => toggleDropdown("caj")}
            className="flex items-center justify-between w-full py-2 px-3 rounded-md text-sm font-medium text-gray-700 hover:bg-indigo-100 hover:text-indigo-700 transition"
          >
            <span className="flex items-center">
              <FontAwesomeIcon icon={faFileInvoiceDollar} className="mr-3 text-green-500" />
              LAPORAN CAJ
            </span>
            <FontAwesomeIcon
              icon={openDropdown === "caj" ? faChevronUp : faChevronDown}
              className="text-gray-500 text-xs"
            />
          </button>
          {openDropdown === "caj" && (
            <div className="ml-6 mt-1 space-y-1">
              <NavLink
                to="/laporan-caj/bulanan"
                onClick={() => setSidebarOpen(false)}
                className={subLinkClass}
              >
                BULANAN
              </NavLink>
            </div>
          )}
        </div>

        <div>
          <button
            onClick={() => toggleDropdown("bayaran")}
            className="flex items-center justify-between w-full py-2 px-3 rounded-md text-sm font-medium text-gray-700 hover:bg-indigo-100 hover:text-indigo-700 transition"
          >
            <span className="flex items-center">
              <FontAwesomeIcon icon={faMoneyBillWave} className="mr-3 text-yellow-500" />
              LAPORAN BAYARAN
            </span>
            <FontAwesomeIcon
              icon={openDropdown === "bayaran" ? faChevronUp : faChevronDown}
              className="text-gray-500 text-xs"
            />
          </button>
          {openDropdown === "bayaran" && (
            <div className="ml-6 mt-1 space-y-1">
              {["HARIAN", "MINGGUAN", "BULANAN"].map((type) => (
                <NavLink
                  key={type}
                  to={`/laporan-bayaran/${type.toLowerCase()}`}
                  onClick={() => setSidebarOpen(false)}
                  className={subLinkClass}
                >
                  {type}
                </NavLink>
              ))}
            </div>
          )}
        </div>

        <div>
          <button
            onClick={() => toggleDropdown("tunggakan")}
            className="flex items-center justify-between w-full py-2 px-3 rounded-md text-sm font-medium text-gray-700 hover:bg-indigo-100 hover:text-indigo-700 transition"
          >
            <span className="flex items-center">
              <FontAwesomeIcon icon={faExclamationTriangle} className="mr-3 text-red-500" />
              LAPORAN TUNGGAKAN
            </span>
            <FontAwesomeIcon
              icon={openDropdown === "tunggakan" ? faChevronUp : faChevronDown}
              className="text-gray-500 text-xs"
            />
          </button>
          {openDropdown === "tunggakan" && (
            <div className="ml-6 mt-1 space-y-1">
              {["MINGGUAN", "BULANAN"].map((type) => (
                <NavLink
                  key={type}
                  to={`/laporan-tunggakan/${type.toLowerCase()}`}
                  onClick={() => setSidebarOpen(false)}
                  className={subLinkClass}
                >
                  {type}
                </NavLink>
              ))}
            </div>
          )}
        </div>

        <NavLink to="/laporan-bulanan-dbkl" onClick={() => setSidebarOpen(false)} className={linkClass}>
          <FontAwesomeIcon icon={faFileAlt} className="mr-3 text-indigo-500" />
          LAPORAN BULANAN DBKL
        </NavLink>

        <NavLink to="/kpi-pegawai-hasil" onClick={() => setSidebarOpen(false)} className={linkClass}>
          <FontAwesomeIcon icon={faBullseye} className="mr-3 text-pink-500" />
          KPI PEGAWAI HASIL
        </NavLink>

        <NavLink to="/qr-kod" onClick={() => setSidebarOpen(false)} className={linkClass}>
          <FontAwesomeIcon icon={faQrcode} className="mr-3 text-purple-500" />
          QR KOD
        </NavLink>

        <NavLink to="/rampasan-tamat-kontrak" onClick={() => setSidebarOpen(false)} className={linkClass}>
          <FontAwesomeIcon icon={faBuilding} className="mr-3 text-red-500" />
          RAMPASAN DAN TAMAT KONTRAK
        </NavLink>

        <NavLink to="/muat-naik" onClick={() => setSidebarOpen(false)} className={linkClass}>
          <FontAwesomeIcon icon={faUpload} className="mr-3 text-sky-500" />
          MUAT NAIK
        </NavLink>

        <NavLink to="/admin/agent-units" onClick={() => setSidebarOpen(false)} className={linkClass}>
          <FontAwesomeIcon icon={faAddressBook} className="mr-3 text-teal-500" />
          TUGASAN UNIT EJEN
        </NavLink>

        <NavLink to="/admin" onClick={() => setSidebarOpen(false)} className={linkClass}>
          <FontAwesomeIcon icon={faUserShield} className="mr-3 text-teal-500" />
          ADMIN
        </NavLink>

        <NavLink to="/chat" onClick={() => setSidebarOpen(false)} className={linkClass}>
          <FontAwesomeIcon icon={faMessage} className="mr-3 text-teal-500" />
          Remarks
        </NavLink>
      </nav>
    </aside>
  );
}